import type { DefaultizedZoomOptions, ZoomData } from '@mui/x-charts/internals';

/**
 * Helper to get the range (in percents of a reference range) corresponding to a given scale.
 * @param centerRatio {number} The ratio of the point that should not move between the previous and next range.
 * @param scaleRatio {number} The target scale ratio.
 * @returns The range to display.
 */
export const zoomAtPoint = (
  centerRatio: number,
  scaleRatio: number,
  currentZoomData: ZoomData,
  options: DefaultizedZoomOptions,
) => {
  const MIN_RANGE = options.minStart;
  const MAX_RANGE = options.maxEnd;

  const MIN_ALLOWED_SPAN = options.minSpan;

  const minRange = currentZoomData.start;
  const maxRange = currentZoomData.end;

  const point = minRange + centerRatio * (maxRange - minRange);

  let newMinRange = (minRange + point * (scaleRatio - 1)) / scaleRatio;
  let newMaxRange = (maxRange + point * (scaleRatio - 1)) / scaleRatio;

  let minSpillover = 0;
  let maxSpillover = 0;

  if (newMinRange < MIN_RANGE) {
    minSpillover = MIN_RANGE - newMinRange;
    newMinRange = MIN_RANGE;
  }
  if (newMaxRange > MAX_RANGE) {
    maxSpillover = newMaxRange - MAX_RANGE;
    newMaxRange = MAX_RANGE;
  }

  if (minSpillover > 0 && maxSpillover > 0) {
    return [MIN_RANGE, MAX_RANGE];
  }

  newMaxRange += minSpillover;
  newMinRange -= maxSpillover;

  newMinRange = Math.min(MAX_RANGE - MIN_ALLOWED_SPAN, Math.max(MIN_RANGE, newMinRange));
  newMaxRange = Math.max(MIN_RANGE + MIN_ALLOWED_SPAN, Math.min(MAX_RANGE, newMaxRange));

  return [newMinRange, newMaxRange];
};

/**
 * Checks if the new span is valid.
 */
export function isSpanValid(
  minRange: number,
  maxRange: number,
  isZoomIn: boolean,
  option: DefaultizedZoomOptions,
) {
  const newSpanPercent = maxRange - minRange;

  if (
    newSpanPercent < 0 ||
    (isZoomIn && newSpanPercent < option.minSpan) ||
    (!isZoomIn && newSpanPercent > option.maxSpan)
  ) {
    return false;
  }

  if (minRange < option.minStart || maxRange > option.maxEnd) {
    return false;
  }

  return true;
}

function getMultiplier(event: WheelEvent) {
  const ctrlMultiplier = event.ctrlKey ? 3 : 1;

  // DeltaMode: 0 is pixel, 1 is line, 2 is page
  // This is defined by the browser.
  if (event.deltaMode === 1) {
    return 1 * ctrlMultiplier;
  }
  if (event.deltaMode) {
    return 10 * ctrlMultiplier;
  }
  return 0.2 * ctrlMultiplier;
}

/**
 * Get the scale ratio and if it's a zoom in or out from a wheel event.
 */
export function getWheelScaleRatio(event: WheelEvent, step: number) {
  const deltaY = -event.deltaY;
  const multiplier = getMultiplier(event);
  const scaledStep = (step * multiplier * deltaY) / 1000;
  // Clamp the scale ratio between 0.1 and 1.9 so that the zoom is not too big or too small.
  const scaleRatio = Math.min(Math.max(1 + scaledStep, 0.1), 1.9);
  const isZoomIn = deltaY > 0;
  return { scaleRatio, isZoomIn };
}

/**
 * Get the ratio of the point in the horizontal center of the area.
 */
export function getHorizontalCenterRatio(
  point: { x: number; y: number },
  area: { left: number; width: number },
  reverse: boolean,
) {
  const { left, width } = area;
  const ratio = (point.x - left) / width;
  return reverse ? 1 - ratio : ratio;
}

/**
 * Get the ratio of the point in the vertical center of the area.
 */
export function getVerticalCenterRatio(
  point: { x: number; y: number },
  area: { top: number; height: number },
  reverse: boolean,
) {
  const { top, height } = area;
  const ratio = (top - point.y) / height + 1;
  return reverse ? 1 - ratio : ratio;
}

/**
 * Translate the zoom data by a given movement.
 */
export function translateZoom(
  initialZoomData: readonly ZoomData[],
  movement: { x: number; y: number },
  drawingArea: { width: number; height: number },
  optionsLookup: Record<string | number, DefaultizedZoomOptions>,
  filterMode: 'x' | 'y' | 'xy' = 'xy',
) {
  return initialZoomData.map((zoom) => {
    const options = optionsLookup[zoom.axisId];
    if (
      !options ||
      !options.panning ||
      (options.axisDirection === 'x' && filterMode === 'y') ||
      (options.axisDirection === 'y' && filterMode === 'x')
    ) {
      return zoom;
    }
    const min = zoom.start;
    const max = zoom.end;
    const span = max - min;
    const MIN_PERCENT = options.minStart;
    const MAX_PERCENT = options.maxEnd;
    const rawDisplacement = options.axisDirection === 'x' ? movement.x : movement.y;
    const displacement = options.reverse ? -rawDisplacement : rawDisplacement;
    const dimension = options.axisDirection === 'x' ? drawingArea.width : drawingArea.height;
    let newMinPercent = min - (displacement / dimension) * span;
    let newMaxPercent = max - (displacement / dimension) * span;
    if (newMinPercent < MIN_PERCENT) {
      newMinPercent = MIN_PERCENT;
      newMaxPercent = newMinPercent + span;
    }
    if (newMaxPercent > MAX_PERCENT) {
      newMaxPercent = MAX_PERCENT;
      newMinPercent = newMaxPercent - span;
    }
    if (
      newMinPercent < MIN_PERCENT ||
      newMaxPercent > MAX_PERCENT ||
      span < options.minSpan ||
      span > options.maxSpan
    ) {
      return zoom;
    }
    return {
      ...zoom,
      start: newMinPercent,
      end: newMaxPercent,
    };
  });
}
